import { HttpClient } from '@angular/common/http';
import {
  inject,
  Injectable,
  isDevMode,
  signal,
  TemplateRef,
  DOCUMENT
} from '@angular/core';
import { firstValueFrom, interval } from 'rxjs';
import { NotificationService } from './notification.service';
import { NzNotificationComponent } from 'ng-zorro-antd/notification';
import { Meta } from '@angular/platform-browser';

interface UpdateConfig {
  update: 'critical' | 'high' | 'low';
  version: string | null;
}

@Injectable({ providedIn: 'root' })
export class CheckForUpdateService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly notificationService = inject(NotificationService);
  private readonly metaService = inject(Meta);

  private readonly updateChannel = new BroadcastChannel('app-updates');
  private readonly buildVersion = signal<string | undefined>(undefined);

  version = this.buildVersion.asReadonly();

  updateButton!: TemplateRef<{ $implicit: NzNotificationComponent }>;

  init(): void {
    const version = this.metaService.getTag('name=version');
    this.buildVersion.set(version?.content);

    if (!isDevMode()) {
      interval(60 * 1000).subscribe(async () => {
        const config = await this.getUpdateConfig();

        if (config.version !== this.version()) {
          this.updateAction(config);
        }
      });

      this.listenForBroadcasts();
    }
  }

  reloadApp(): void {
    // Broadcasting update to other tabs
    this.updateChannel.postMessage({ type: 'UPDATE_AVAILABLE' });

    this.document.location.reload();
  }

  getUpdateConfig(): Promise<UpdateConfig> {
    const configFile = './assets/update.config.json';

    return firstValueFrom(this.http.get<UpdateConfig>(configFile));
  }

  private updateAction(config: UpdateConfig): void {
    if (config.update === 'critical') {
      this.reloadApp();
      return;
    }

    const title = 'notifications.updateService.title';
    const message = 'notifications.updateService.message';

    this.notificationService.closeAll();
    this.notificationService.info(message, 0, title, {
      nzClass: 'hide-notification-close-btn',
      nzStyle: {
        background: '#e6f4ff'
      },
      nzButton: this.updateButton
    });
  }

  private listenForBroadcasts(): void {
    this.updateChannel.onmessage = (event: MessageEvent): void => {
      if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
        this.document.location.reload();
      }
    };
  }
}
